import { ArrowRight, TrendingUp, Users, MapPin, Star } from "lucide-react";
import { useBookingDialog } from "@/contexts/BookingDialogContext";

export default function Hero() {
  const { openDialog } = useBookingDialog();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 grain"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-5xl reveal" style={{ animationDelay: "0.05s" }}>
          <h1
            data-testid="hero-headline"
            className="font-display text-[clamp(3rem,8vw,7.5rem)] leading-[0.92] tracking-[-0.035em] text-[#0D3A35] font-semibold"
          >
            More foot traffic.
            <br />
            More customers.
            <br />
            More <span className="text-[#276152]">revenue</span>.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-2xl text-lg sm:text-xl leading-relaxed text-[#0D3A35]/70"
          >
            Lychee connects boutique hotels, restaurants, clinics, and retail brands with vetted UGC creators who actually <span className="text-[#276152] font-semibold">visit</span>, film, and post about your space &mdash; driving walk-ins, customers, bookings and revenue for local businesses across MENA &amp; South Asia.
          </p>

          <div className="mt-10">
            <button
              type="button"
              data-testid="hero-book-consultation"
              onClick={() => openDialog()}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#0D3A35] text-white text-sm font-semibold hover:bg-[#276152] transition-colors"
            >
              Book consultation
              <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </button>
          </div>
        </div>

        {/* Product mockup card */}
        <div
          className="mt-20 relative reveal"
          style={{ animationDelay: "0.25s" }}
        >
          <div className="relative bg-[#F6F5F1] rounded-[2rem] border border-[#B1B7AB]/40 p-6 sm:p-10 overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-8 items-stretch">
              {/* Left mockup — campaign brief card */}
              <div className="lg:col-span-7 bg-white rounded-2xl border border-[#B1B7AB]/40 p-6 sm:p-8 shadow-[0_30px_60px_-30px_rgba(13,58,53,0.18)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#276152]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#276152]">
                      Campaign · Dubai
                    </span>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0D3A35]/55">
                    Visit + Shoot
                  </span>
                </div>

                <h3 className="font-display text-3xl mt-4 text-[#0D3A35]">
                  Verde Spa &mdash; weekend coverage
                </h3>
                <p className="mt-1 text-sm text-[#0D3A35]/60">
                  3 creators · 2 visits · 1 reel · 5 stories · 8 stills
                </p>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[
                    { name: "Maya H.", role: "Hospitality", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80" },
                    { name: "Sofia L.", role: "Wellness", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80" },
                    { name: "Imani O.", role: "Lifestyle", img: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=200&q=80" },
                  ].map((c) => (
                    <div
                      key={c.name}
                      className="bg-[#F6F5F1] border border-[#B1B7AB]/40 rounded-xl p-3"
                    >
                      <div className="aspect-square overflow-hidden rounded-lg">
                        <img src={c.img} alt={c.name} className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs font-semibold text-[#0D3A35] mt-2">
                        {c.name}
                      </p>
                      <p className="text-[10px] text-[#276152]">{c.role}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between pt-5 border-t border-[#B1B7AB]/40">
                  <div className="flex items-center gap-2 text-xs text-[#0D3A35]/65">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                    Verde Spa · DIFC, Dubai
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#276152]">
                    Confirmed
                    <span className="w-1.5 h-1.5 rounded-full bg-[#276152]" />
                  </span>
                </div>
              </div>

              {/* Right stats stack */}
              <div className="lg:col-span-5 grid grid-cols-2 lg:grid-cols-1 gap-4">
                <div className="bg-[#0D3A35] text-white rounded-2xl p-6 flex-1">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#B1B7AB]">
                    <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} />
                    Avg. walk-in lift
                  </div>
                  <div className="font-display text-6xl mt-3 leading-none">
                    3.4×
                  </div>
                  <p className="text-xs text-white/70 mt-2">
                    within 14 days of a visit campaign
                  </p>
                </div>

                <div className="bg-white border border-[#B1B7AB]/40 rounded-2xl p-6 flex-1">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#276152]">
                    <Users className="w-3.5 h-3.5" strokeWidth={2} />
                    Creators ready
                  </div>
                  <div className="font-display text-5xl mt-3 leading-none text-[#0D3A35]">
                    200+
                  </div>
                  <p className="text-xs text-[#0D3A35]/65 mt-2">
                    vetted, on-ground · MENA &amp; South Asia
                  </p>
                </div>

                <div className="bg-white border border-[#B1B7AB]/40 rounded-2xl p-6 col-span-2 lg:col-span-1">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#276152]">
                    <Star className="w-3.5 h-3.5 fill-[#276152]" strokeWidth={0} />
                    Show-up rate
                  </div>
                  <div className="font-display text-5xl mt-3 leading-none text-[#0D3A35]">
                    98%
                  </div>
                  <p className="text-xs text-[#0D3A35]/65 mt-2">
                    creators who arrive on time, every time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
